import { ColumnType } from '../types';

/** State-entry key for a column: its `settingsKey` alias, else its `key`. */
export function stateKeyOf<R>(col: ColumnType<R>): string {
  return col.settingsKey ?? col.key;
}

/** A column with child leaves (a header group), as opposed to a leaf column. */
export function isGroup<R>(col: ColumnType<R>): boolean {
  return Array.isArray(col.children) && col.children.length > 0;
}

/** Consumer-injected structural column (actions/selection/expand). */
export function isSystem<R>(col: ColumnType<R>): boolean {
  return col.system === true;
}

/** Panel/label text for a column: `editTitle`, else a string `title`, else `key`. */
export function labelOf<R>(col: ColumnType<R>): string {
  if (col.editTitle) return col.editTitle;
  if (typeof col.title === 'string') return col.title;
  return col.key;
}

/**
 * Cascaded meta — `configurable`/`defaultEnabled`/`defaultPinned`/`pinnable`
 * set on a group provide the default for every child; an explicit child value
 * always wins. `reorderable` deliberately does NOT cascade (on a group it
 * governs moving the group itself, on a child moving within the group).
 */
export function effectiveConfigurable<R>(col: ColumnType<R>, parent?: ColumnType<R>): boolean {
  return (col.configurable ?? parent?.configurable) !== false;
}

export function effectivePinnable<R>(col: ColumnType<R>, parent?: ColumnType<R>): boolean {
  return (col.pinnable ?? parent?.pinnable) !== false;
}

export function effectiveDefaultEnabled<R>(col: ColumnType<R>, parent?: ColumnType<R>): boolean {
  return (col.defaultEnabled ?? parent?.defaultEnabled) !== false;
}

export function effectiveDefaultPinned<R>(col: ColumnType<R>, parent?: ColumnType<R>): boolean {
  return (col.defaultPinned ?? parent?.defaultPinned) === true;
}

export interface SystemSlot<R> {
  col: ColumnType<R>;
  index: number;
}

/**
 * Split one level's columns into user-movable columns and system columns paired
 * with their original index, so ordering can operate on the movable set alone.
 */
export function partitionSystem<R>(columns: ColumnType<R>[]): {
  movable: ColumnType<R>[];
  system: SystemSlot<R>[];
} {
  const movable: ColumnType<R>[] = [];
  const system: SystemSlot<R>[] = [];
  columns.forEach((col, index) => {
    if (isSystem(col)) system.push({ col, index });
    else movable.push(col);
  });
  return { movable, system };
}

/**
 * Re-insert system columns among the reordered movable columns at their original
 * indices (ascending), so structural columns hold their absolute slot regardless
 * of user ordering.
 */
export function reattachSystem<R>(
  movable: ColumnType<R>[],
  system: SystemSlot<R>[],
): ColumnType<R>[] {
  const result = [...movable];
  [...system]
    .sort((a, b) => a.index - b.index)
    .forEach(({ col, index }) => {
      result.splice(Math.min(index, result.length), 0, col);
    });
  return result;
}
